import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";

import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import ErrorState from "../../components/ui/ErrorState";
import RoleBadge from "../../components/ui/RoleBadge";
import ScopePicker from "../../components/ui/ScopePicker";
import SingleSensorPicker from "../../components/ui/SingleSensorPicker";
import Spinner from "../../components/ui/Spinner";
import {
  useMembers,
  useAccessGrants,
  putPendingAccessGrant,
  revokeInvitation,
  pendingGrantKey,
  apiMessage,
  MEMBERS_QUERY_KEY,
  GRANTS_QUERY_KEY,
} from "../../services/people";
import {
  draftFromGrant,
  draftToGrantBody,
  grantsNothing,
  type GrantDraft,
} from "../../services/grantDraft";
import { getSensorGroups } from "../Events/unitEventsUtils";

/**
 * One invitee: the role they will have, the sensors they will reach.
 *
 * The same shape as the member page, because an admin should not have to learn a
 * second screen for somebody who happens not to have signed in yet. What differs is
 * what can be changed, and that difference is stated rather than hidden:
 *
 * - **Sensor access can be set**, and is stored against the invitation until they
 *   accept (REQ-ACL-23).
 * - **The role cannot.** Auth0 has no update-invitation operation — create, get and
 *   delete only — so changing it means withdrawing and inviting again, which
 *   invalidates the link they may already be holding. Shown read-only with that
 *   said, rather than offered as a picker that silently could not work.
 */
export default function PendingInviteePage() {
  const { invitationId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const members = useMembers();
  const grants = useAccessGrants();

  const member = (members.data ?? []).find(
    (m) => m.pending && m.invitationId === invitationId
  );

  const grant = member?.email
    ? (grants.data ?? []).find((g) => g.user_id === pendingGrantKey(member.email!))
    : undefined;

  const properties = useQuery({
    queryKey: ["admin", "properties"],
    queryFn: async () => {
      const response = await getSensorGroups();
      return ((response?.data ?? []) as { id: string; name: string }[]).map((g) => ({
        buildingId: g.id,
        name: g.name,
      }));
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const [draft, setDraft] = useState<GrantDraft | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seeded once the grant has been read and never re-seeded: re-seeding would
  // throw away whatever the admin had ticked the moment a refetch resolved. The
  // same contract the member editor states, for the same reason.
  useEffect(() => {
    if (draft === null && grants.isSuccess && member) {
      setDraft(draftFromGrant(grant));
    }
  }, [draft, grants.isSuccess, member, grant]);

  const save = useMutation({
    mutationFn: async () =>
      putPendingAccessGrant(invitationId, draftToGrantBody(draft!)),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: GRANTS_QUERY_KEY });
    },
    onError: (e) =>
      setError(apiMessage(e, "We couldn't save that. Please try again.")),
  });

  const withdraw = useMutation({
    mutationFn: async () => revokeInvitation(invitationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: GRANTS_QUERY_KEY });
      navigate("/admin/people");
    },
    onError: (e) =>
      setError(
        apiMessage(e, "We couldn't withdraw that invitation. Please try again.")
      ),
  });

  if (members.isPending) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size={96} />
      </div>
    );
  }

  if (!member) {
    return (
      <ErrorState
        title="We couldn't find that invitation"
        message="It may have been withdrawn, or already accepted."
        onRetry={() => navigate("/admin/people")}
      />
    );
  }

  const isUserTier = member.tier === "User";
  const changed =
    draft !== null &&
    JSON.stringify(draftToGrantBody(draft)) !==
      JSON.stringify(draftToGrantBody(draftFromGrant(grant)));

  return (
    <>
      <nav aria-label="Breadcrumb" className="mb-5">
        <button
          type="button"
          onClick={() => {
            if (
              changed &&
              !window.confirm(
                "Discard your unsaved changes?\n\n" +
                  "You've changed this person's sensor access but haven't saved it yet."
              )
            ) {
              return;
            }
            navigate("/admin/people");
          }}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-chip border border-primary/45 bg-primary/15 text-primary hover:bg-primary/25"
        >
          <ArrowBackIosIcon fontSize="small" />
          <span className="font-semibold">Back to People</span>
        </button>
      </nav>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-white">{member.email}</h1>
        <p className="text-ink-3 mt-1">
          Invited &middot; hasn't accepted yet
        </p>
      </div>

      <div className="space-y-4">
        <Card tone="light">
          <h2 className="text-lg font-semibold mb-1">Role</h2>
          {/* Read-only HERE, and only here. Auth0 has no update-invitation
              operation -- create, get and delete only -- so a pending role cannot
              be amended in place; it can be changed freely from their member page
              once they accept. An earlier version of this copy said only
              "to change it, withdraw and send a new one", which reads as the role
              being fixed for good. It is not, and saying so sent an admin to
              withdraw an invitation they did not need to touch. */}
          <p className="text-ink-2 text-sm mb-3">
            Chosen when they were invited. You can change it from their page once
            they accept, or it can be changed by withdrawing this invitation and
            sending a new one.
          </p>
          <RoleBadge tier={member.tier ?? "None"} label={member.tierLabel ?? ""} />
        </Card>

        <Card tone="light">
          <h2 className="text-lg font-semibold mb-1">Sensor access</h2>
          <p className="text-ink-2 text-sm mb-4">
            This applies as soon as they sign in.
          </p>

          {properties.isError || grants.isError ? (
            <p className="text-alert-critical text-sm">
              We couldn't load what they'd be choosing from. Reload to try again.
            </p>
          ) : draft === null ? (
            <div className="flex justify-center py-6">
              <Spinner size={48} />
            </div>
          ) : (
            <>
              {isUserTier ? (
                <SingleSensorPicker
                  value={draft}
                  properties={properties.data ?? []}
                  onChange={setDraft}
                  disabled={save.isPending}
                />
              ) : (
                <ScopePicker
                  value={draft}
                  properties={properties.data ?? []}
                  onChange={setDraft}
                  disabled={save.isPending}
                />
              )}

              {grantsNothing(draft) && !isUserTier && (
                <p className="text-ink-2 text-sm mt-3">
                  This reaches no sensors. They'll be able to sign in and see
                  nothing.
                </p>
              )}

              <div className="flex items-center gap-3 mt-4">
                <Button
                  onClick={() => save.mutate()}
                  disabled={!changed || save.isPending}
                >
                  {save.isPending ? "Saving…" : "Save access"}
                </Button>
                {!changed && !save.isPending && save.isSuccess && (
                  <span className="text-ink-2 text-sm">Saved.</span>
                )}
              </div>
            </>
          )}
        </Card>

        <Card tone="light">
          <h2 className="text-lg font-semibold mb-1">Withdraw this invitation</h2>
          <p className="text-ink-2 text-sm mb-4">
            {member.email} won't be able to accept it, and any access chosen here
            is removed with it. You can invite them again afterwards.
          </p>

          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="danger"
              disabled={withdraw.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    `Withdraw the invitation to ${member.email}?`
                  )
                ) {
                  withdraw.mutate();
                }
              }}
            >
              {withdraw.isPending ? "Withdrawing…" : "Withdraw"}
            </Button>
          </div>
        </Card>

        {error && (
          <Card tone="light">
            <p className="text-alert-critical text-sm" role="alert">
              {error}
            </p>
          </Card>
        )}
      </div>
    </>
  );
}
